import { useId, useState } from "react";
import { actionFor } from "../emailActions";
import { MailDirection, ProcessingStatus } from "../EmailSearchAPI";
import { ActionResult } from "../useEmailDetail";

interface iActionBarProps {
    direction: MailDirection;
    /** Nullable on the wire; `actionFor` refuses to offer an action without one. */
    status?: ProcessingStatus;
    /** Server-computed. An input to inbound eligibility (spec §3.1). */
    stalled: boolean;
    sending: boolean;
    result?: ActionResult;
    /** Called with whatever is in the reason field, which may be empty. */
    onSubmit: (reason?: string) => void;
}

/**
 * What to say about this direction's one action, at each of the three
 * moments the copy differs: `resting` (offered but not yet armed), `armed`
 * (armed, awaiting confirmation) and `queued` (the server has accepted it).
 *
 * One entry per direction, not three tables (two `Record`s plus a bare
 * `direction === "Inbound" ? … : …` for the third) picked apart independently.
 * That was the exact shape `Timeline.tsx`'s `OUTCOME_TREATMENT` doc comment
 * warns against: a third `MailDirection` would fail to compile against the
 * two `Record`s here, yet the ternary has no such backstop — it would just
 * fall through to the `Outbound` string, correct on two strings out of three
 * and silently wrong on the one nobody added a case for. Folding all three
 * into one entry per direction makes them impossible to update independently
 * and forces a single compiler error, not two misses and a silent one, the
 * day a third direction is added.
 *
 * The `armed` and `queued` sentences both end at "queued"/"has not … yet" on
 * purpose. Contract §2.8: the work has not happened when the response
 * arrives, and the copy must not imply it will have by the time the operator
 * looks away.
 */
const DIRECTION_COPY: Record<MailDirection, { resting: string; armed: string; queued: string }> = {
    Inbound: {
        resting: "Re-queues this email from the start.",
        armed:
            "It will be re-queued from the start and its status set to Queued. The work runs on the consumer " +
            "afterwards — nothing is reprocessed by the time this page responds.",
        queued:
            "The status below has been updated and the entry added to the timeline. This email has not been " +
            "reprocessed yet.",
    },
    Outbound: {
        resting: "Queues a fresh build, or re-arms the send counter.",
        armed:
            "A fresh build is queued, or — if the email was abandoned — its send counter is re-armed. Either way " +
            "the work runs afterwards; nothing is resent by the time this page responds.",
        queued:
            "The status below has been updated and the entry added to the timeline. This email has not been " +
            "rebuilt or resent yet.",
    },
};

function Banner({ direction, result }: { direction: MailDirection; result: ActionResult }) {
    if (result.kind === "queued") {
        return (
            <div
                role="status"
                className="mp-banner mp-banner-queued"
            >
                <p>
                    <b>Queued.</b> {DIRECTION_COPY[direction].queued}
                </p>
                <p>Refresh to see whether it got further.</p>
            </div>
        );
    }

    if (result.kind === "refused") {
        return (
            <div
                role="status"
                className="mp-banner mp-banner-refused"
            >
                {/*
                    NOT an error panel. A 400 here is a precondition the server
                    declined — nothing changed, and one of the four messages
                    means nothing is wrong at all. The server's own sentences
                    are rendered verbatim rather than matched against, so a
                    typo fix upstream cannot silently change how this reads.
                */}
                <p>
                    <b>Not queued.</b> Nothing was changed.
                </p>
                {result.messages.length > 0 ? (
                    <ul>
                        {/*
                            Keyed on the index as well as the text: a 400 can
                            repeat the same sentence across two fields, so the
                            message alone is not unique.
                        */}
                        {result.messages.map((message, index) => (
                            <li key={`${index}-${message}`}>{message}</li>
                        ))}
                    </ul>
                ) : (
                    <p>The server refused the request and did not say why.</p>
                )}
            </div>
        );
    }

    return (
        <div
            role="status"
            className="mp-banner mp-banner-failed"
        >
            <p>
                <b>The request failed.</b> Nothing was changed. Try again, or refresh to check the current state first.
            </p>
        </div>
    );
}

/**
 * The one support action this email offers, if any.
 *
 * ELIGIBILITY IS DERIVED FROM STATUS, so an ineligible email shows a disabled
 * control with the reason beside it rather than a button that would `400`
 * (spec §7.1). That also means the optimistic status flip after a successful
 * action disables the control by itself — there is no separate "submitted"
 * flag that could fall out of sync with what is on screen.
 *
 * Arming is a SEPARATE CLICK from confirming. One interaction does both jobs
 * the contract asks for: it prompts for the optional audit note, and it
 * guards a write that this page cannot undo. There is no modal, drawer or
 * dialog anywhere in this app, and this is deliberately not the first.
 *
 * The result banner is rendered ALONGSIDE the control, not in place of it —
 * the button and its eligibility stay visible so the operator can see what,
 * if anything, is offered next. The generic "why" / description line next to
 * the button renders alongside the banner too (spec §5.2): the banner reports
 * what just happened, and the why-text explains why the control looks the
 * way it does right now. They say different things and both stay on screen.
 */
function ActionBar({ direction, status, stalled, sending, result, onSubmit }: iActionBarProps) {
    const [armed, setArmed] = useState(false);
    const [reason, setReason] = useState("");
    const verdict = actionFor(direction, status, stalled);
    // Not a literal: a page with two `ActionBar`s would collide on a hardcoded
    // id, and a colliding `htmlFor` focuses the WRONG input — worse than an
    // ambiguous test query. Not reachable today (the detail view replaces the
    // worklist, one `ActionBar` per page), but `useId` removes the footgun for
    // two lines.
    const reasonId = useId();

    if (!verdict.available) {
        return (
            <div className="mp-actions mp-actions-off">
                <div className="mp-action-row">
                    <button
                        type="button"
                        className="g-btn btn-default"
                        disabled
                    >
                        {verdict.action}
                    </button>
                    <p className="mp-action-why">{verdict.why}</p>
                </div>
                {result && (
                    <Banner
                        direction={direction}
                        result={result}
                    />
                )}
            </div>
        );
    }

    return (
        <div className="mp-actions">
            {armed ? (
                <div className="mp-confirm">
                    <div className="mp-confirm-head">{verdict.action} this email?</div>
                    <p className="mp-action-why">{DIRECTION_COPY[direction].armed}</p>
                    <div>
                        <label htmlFor={reasonId}>Reason for the audit trail (optional)</label>
                        <input
                            id={reasonId}
                            type="text"
                            autoComplete="off"
                            value={reason}
                            onChange={event => setReason(event.target.value)}
                        />
                    </div>
                    <div className="mp-action-row">
                        <button
                            type="button"
                            className="g-btn btn-primary"
                            disabled={sending}
                            onClick={() => {
                                // Sent whether or not the field was filled. The
                                // note is optional on the wire and stays optional
                                // here; SendEmailAction drops a blank one.
                                onSubmit(reason);
                                setArmed(false);
                                setReason("");
                            }}
                        >
                            {sending ? "Sending…" : `Confirm ${verdict.action.toLowerCase()}`}
                        </button>
                        <button
                            type="button"
                            className="g-btn btn-default"
                            disabled={sending}
                            onClick={() => {
                                setArmed(false);
                                setReason("");
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            ) : (
                <div className="mp-action-row">
                    <button
                        type="button"
                        className="g-btn btn-primary"
                        disabled={sending}
                        onClick={() => setArmed(true)}
                    >
                        {sending ? "Sending…" : verdict.action}
                    </button>
                    <p className="mp-action-why">{DIRECTION_COPY[direction].resting}</p>
                </div>
            )}
            {result && (
                <Banner
                    direction={direction}
                    result={result}
                />
            )}
        </div>
    );
}

export default ActionBar;
